import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  SUPPORT_EMAIL,
  SUPPORT_ISSUE_TYPES,
  openSupportTicketEmail,
  type SupportIssueType,
  type SupportTicketFormValues,
} from '@/shared/utils/email-utils';

export interface SupportTicketDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Prefill company (operator) when known from current org context. */
  defaultCompany?: string;
}

interface FormState {
  company: string;
  serviceLineNumbers: string;
  terminalId: string;
  issueTypes: SupportIssueType[];
  description: string;
}

function emptyForm(defaultCompany = ''): FormState {
  return {
    company: defaultCompany,
    serviceLineNumbers: '',
    terminalId: '',
    issueTypes: [],
    description: '',
  };
}

/**
 * Captures support ticket details in-app, then opens the user's mail client
 * with a plain-text body matching the Orbius support template.
 */
const SupportTicketDialog = ({
  isOpen,
  onClose,
  defaultCompany = '',
}: SupportTicketDialogProps) => {
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultCompany));
  const [errors, setErrors] = useState<{ issueTypes?: string; description?: string }>({});

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm(defaultCompany));
      setErrors({});
    }
  }, [isOpen, defaultCompany]);

  const toggleIssueType = (type: SupportIssueType, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      issueTypes: checked
        ? [...prev.issueTypes, type]
        : prev.issueTypes.filter((t) => t !== type),
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (form.issueTypes.length === 0) {
      nextErrors.issueTypes = 'Select at least one issue type';
    }
    if (!form.description.trim()) {
      nextErrors.description = 'Please describe the issue';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const values: SupportTicketFormValues = {
      company: form.company,
      serviceLineNumbers: form.serviceLineNumbers,
      terminalId: form.terminalId,
      issueTypes: form.issueTypes,
      description: form.description,
    };
    openSupportTicketEmail(values);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Contact support</DialogTitle>
          <DialogDescription>
            Fill in the ticket below. We&apos;ll open your email app with a message ready to send to{' '}
            {SUPPORT_EMAIL}. You can attach images or files in your mail client before sending.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <section className="space-y-3">
            <h3 className="text-sm font-bold">1) Account and related resources information:</h3>
            <div className="space-y-2">
              <Label htmlFor="support-company">Company (operator)</Label>
              <Input
                id="support-company"
                value={form.company}
                onChange={(e) => setForm((prev) => ({ ...prev, company: e.target.value }))}
                placeholder="Company / operator name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-sl">Service line number(s)</Label>
              <Input
                id="support-sl"
                value={form.serviceLineNumbers}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, serviceLineNumbers: e.target.value }))
                }
                placeholder="e.g. SL-100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-terminal">Terminal ID / kit serial</Label>
              <Input
                id="support-terminal"
                value={form.terminalId}
                onChange={(e) => setForm((prev) => ({ ...prev, terminalId: e.target.value }))}
              />
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-bold">2) Issue type — mark at least one with [x]:</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {SUPPORT_ISSUE_TYPES.map((type) => {
                const id = `issue-${type}`;
                const checked = form.issueTypes.includes(type);
                return (
                  <div
                    key={type}
                    className="flex items-center space-x-2 rounded-md border px-3 py-2 hover:bg-muted/50"
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={(value) => toggleIssueType(type, value === true)}
                    />
                    <Label htmlFor={id} className="cursor-pointer font-normal leading-snug">
                      {type}
                    </Label>
                  </div>
                );
              })}
            </div>
            {errors.issueTypes && (
              <p className="text-sm text-destructive">{errors.issueTypes}</p>
            )}
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-bold">3) Ticket description:</h3>
            <Textarea
              id="support-description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the issue. Do not hesitate to attach images/files in your email to facilitate support."
              rows={4}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description}</p>
            )}
          </section>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Open email to send</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportTicketDialog;
